/* tslint:disable:jsx-no-lambda WI:47647 */
import * as React from 'react';
import CompleteCheckbox from './CompleteCheckbox';
import EditableTitle from './EditableTitle';
import getTodoItemViewState from '../selectors/getTodoItemViewState';
import isBeingEdited from '../selectors/isBeingEdited';
import isImportant from '../utils/isImportant';
import isTaskComplete from '../utils/isTaskComplete';
import Todo from '../store/schema/Todo';
import TodoListItemInfo from './TodoListItemInfo';
import TodoStarCharm from './TodoStarCharm';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { clickTodo, updateIsEditing } from '../actions/todoListItemViewStateActions';
import { ControlIcons } from 'owa-control-icons';
import { FocusZone, FocusZoneDirection, IFocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import { getAnchorForContextMenu } from 'owa-positioning';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { KeyboardEvent } from 'react';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { markTodoComplete, markTodoIncomplete, updateTodoSubject } from '../actions/todoActions';
import { observer } from 'mobx-react';
import { showTodoContextMenu } from '../actions/todoFolderViewStateActions';
import { star, unstar } from '../actions/todoActions';
import { getRTLSafeKeyCode } from '@uifabric/utilities';
import { isBrowserFirefox } from 'owa-user-agent';

let styles = require('./TodoListItem.scss');
let classNames = require('classnames/bind').bind(styles);

export interface TodoListItemProps {
    todo: Todo;

    /**
     * Whether this item supports items being dropped over it.
     * @default true
     */
    canDropOver?: boolean;

    /**
     * Optional class name for the title
     */
    titleClassName?: string;

    /**
     * Optional class name for the complete checkbox
     */
    checkboxClassName?: string;
}

@observer
export default class TodoListItem extends React.Component<TodoListItemProps, {}> {
    static defaultProps = {
        canDropOver: true,
    };

    private taskItem: HTMLDivElement;
    private innerFocus: IFocusZone;
    private editableTitle: EditableTitle;

    private forwardtaskItemRef = ref => (this.taskItem = ref);
    private forwardInnerFocusRef = ref => (this.innerFocus = ref);
    private forwardEditableTitleRef = ref => (this.editableTitle = ref);

    render() {
        const todo = this.props.todo;
        const canDropOver = this.props.canDropOver;
        const todoViewState = getTodoItemViewState(todo.Id);
        const isDragOver =
            canDropOver && todoViewState.drop.isDragOver && !todoViewState.drag.isBeingDragged;
        const isEditing = isBeingEdited(todo.Id);
        const important = isImportant(todo.Importance);
        const ariaPropsRow: AriaProperties = { role: AriaRoles.row };
        const ariaPropsGridCell: AriaProperties = { role: AriaRoles.gridcell };

        return (
            <div
                ref={this.forwardtaskItemRef}
                className={styles.taskItem}
                onContextMenu={this.onItemContextMenu}
                onKeyDown={this.onKeyDown}
                data-is-focusable={true}
                {...generateDomPropertiesForAria(ariaPropsRow)}>
                <FocusZone
                    direction={FocusZoneDirection.horizontal}
                    componentRef={this.forwardInnerFocusRef}>
                    <div
                        className={classNames(
                            styles.body,
                            { isEditing: isEditing },
                            { topBorderDragOver: isDragOver }
                        )}
                        onClick={this.onClick}>
                        <Icon className={styles.bar1} iconName={ControlIcons.MoreVertical} />
                        <Icon className={styles.bar2} iconName={ControlIcons.MoreVertical} />
                        <div {...generateDomPropertiesForAria(ariaPropsGridCell)}>
                            <CompleteCheckbox
                                isComplete={isTaskComplete(todo.Status)}
                                onToggleComplete={this.toggleComplete.bind(this, todo)}
                                checkboxClassName={this.props.checkboxClassName}
                                {...generateDomPropertiesForAria(ariaPropsGridCell)}
                            />
                        </div>
                        <div
                            className={styles.titleWrapper}
                            {...generateDomPropertiesForAria(ariaPropsGridCell)}>
                            <EditableTitle
                                ref={this.forwardEditableTitleRef}
                                isEditing={isEditing}
                                title={todo.Subject}
                                titleStyle={classNames(
                                    {
                                        completedTitle: isTaskComplete(todo.Status),
                                    },
                                    styles.title
                                )}
                                textFieldProps={{
                                    maxLength: 255,
                                }}
                                titleWrapperStyle={this.props.titleClassName}
                                onExitEdit={() => this.onUpdateIsEditing(false)}
                                onEnterEdit={() => this.onUpdateIsEditing(true)}
                                onSaveTitle={(subject: string) => {
                                    if (todo.Subject !== subject) {
                                        updateTodoSubject(todo.Id, subject);
                                    }
                                }}
                                moveFocusAway={() => this.setFocus()}
                                onTextFieldKeyDown={this.onTextFieldKeyDown}
                            />
                            <TodoListItemInfo todo={todo} />
                        </div>
                        <div {...generateDomPropertiesForAria(ariaPropsGridCell)}>
                            <TodoStarCharm
                                onClick={!important ? () => star(todo.Id) : () => unstar(todo.Id)}
                                isImportant={important}
                                style={classNames(styles.charm, { normalImportance: important })}
                            />
                        </div>
                    </div>
                </FocusZone>
            </div>
        );
    }

    private onUpdateIsEditing = (isEditing: boolean) => {
        updateIsEditing(this.props.todo.Id, isEditing);
    };

    public isDraggable = () => {
        /**
         * In Firefox, when a textarea is draggable, you are no longer able to click
         * to move the cursor within a textarea. This bug is tracked here: https://bugzilla.mozilla.org/show_bug.cgi?id=943944
         * A textarea is rendered by the EditableTitle component when isEditing is false,
         * so when isEditing, we want to disable dragging for the todo.
         */
        const todo = this.props.todo;
        const isEditing = isBeingEdited(todo.Id);
        return !(isBrowserFirefox() && isEditing);
    };

    public setFocus() {
        this.taskItem.focus();
    }

    private onKeyDown = (target: KeyboardEvent<EventTarget>) => {
        switch (target.keyCode) {
            case getRTLSafeKeyCode(KeyCodes.right):
                this.innerFocus.focus();
                target.preventDefault();
                target.stopPropagation();
                break;
        }
    };

    private onTextFieldKeyDown = (target: KeyboardEvent<EventTarget>) => {
        switch (target.keyCode) {
            /**
             * Stop the propogation of the left and right arrow key presses unless the cursor is at the
             * start or end of the the text so that consuming component's key handlers do not interfere
             * with the natural behavior of the TextArea.
             */
            case getRTLSafeKeyCode(KeyCodes.right):
                if (!this.editableTitle.isCursorAtEnd() || this.editableTitle.isTextSelected()) {
                    // User is interacting with the editable control if:
                    // - cursor is within the text (not at end of text)
                    // - OR text is selected (even if the cursor is at the end of the text)
                    target.stopPropagation();
                }
                break;
            case getRTLSafeKeyCode(KeyCodes.left):
                if (!this.editableTitle.isCursorAtStart() || this.editableTitle.isTextSelected()) {
                    // User is interacting with the editable control if:
                    // - cursor is within the text (not at start of text)
                    // - OR text is selected (even if the cursor is at the start of the text)
                    target.stopPropagation();
                }
                break;
            case KeyCodes.down:
            case KeyCodes.up:
            case KeyCodes.home:
            case KeyCodes.end:
                // Always keep these within editable control
                target.stopPropagation();
                break;
        }
    };

    private onClick = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        clickTodo(this.props.todo.Id);
    };

    private toggleComplete(todo: Todo) {
        if (!isTaskComplete(todo.Status)) {
            markTodoComplete(todo.Id);
        } else {
            markTodoIncomplete(todo.Id);
        }
    }

    /**
     * The onContextMenu event handler which opens the context menu
     * @param evt the mouse event
     */
    private onItemContextMenu = (evt: React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
        evt.preventDefault();

        showTodoContextMenu(this.props.todo.Id, getAnchorForContextMenu(evt));
    };
}
